import type { Cadence } from './reportEnums.constants';
import { parseMeasurementPeriodEndYearMo } from './reportMeasurementPeriod.util';

const MONTH_ABBREV_TO_NUM: Record<string, string> = {
  Jan: '01',
  Feb: '02',
  Mar: '03',
  Apr: '04',
  May: '05',
  Jun: '06',
  Jul: '07',
  Aug: '08',
  Sep: '09',
  Oct: '10',
  Nov: '11',
  Dec: '12',
};

/**
 * Parses a short month label such as `Aug 2024` into `YYYYMM`.
 * @param label - Month label from a composed reporting period.
 * @returns Six-digit year-month string or null when not parseable.
 */
export const parseReportMonthLabelToYearMo = (label: string): string | null => {
  const trimmed = label.trim();
  const match = /^([A-Za-z]{3})\s+(\d{4})$/.exec(trimmed);
  if (!match) {
    return null;
  }
  const monthNum = MONTH_ABBREV_TO_NUM[match[1]];
  if (!monthNum) {
    return null;
  }
  return `${match[2]}${monthNum}`;
};

/**
 * Derives the warehouse `year_mo` key from a persisted report period label.
 * Monthly reports use the end month; quarterly reports use the quarter end month
 * inside the parenthetical range. JOC (adhoc) periods have no single `year_mo`.
 * @param reportingPeriod - Stored `reports.reporting_period` label.
 * @param cadence - Report cadence.
 * @returns `YYYYMM` for monthly/quarterly, or null for adhoc / unparsable labels.
 */
export const reportingPeriodToYearMo = (
  reportingPeriod: string,
  cadence: Cadence,
): string | null => {
  const period = reportingPeriod.trim();
  if (!period || cadence === 'adhoc') {
    return null;
  }

  if (cadence === 'monthly') {
    if (period.includes('–')) {
      const end = period.split('–').pop()?.trim() ?? '';
      return parseReportMonthLabelToYearMo(end);
    }
    return parseReportMonthLabelToYearMo(period);
  }

  const rangeMatch = /\(([^)]+)\)\s*$/.exec(period);
  if (rangeMatch) {
    const end = rangeMatch[1].split('–').pop()?.trim() ?? '';
    return parseReportMonthLabelToYearMo(end);
  }

  return null;
};

const MONTH_NUM_TO_ABBREV: Record<string, string> = {
  '01': 'Jan',
  '02': 'Feb',
  '03': 'Mar',
  '04': 'Apr',
  '05': 'May',
  '06': 'Jun',
  '07': 'Jul',
  '08': 'Aug',
  '09': 'Sep',
  '10': 'Oct',
  '11': 'Nov',
  '12': 'Dec',
};

/** Default number of months in the member engagement trend chart. */
export const ENGAGEMENT_TREND_MONTH_COUNT = 6;

/**
 * Formats `YYYYMM` as a report month label (`Dec 2025`).
 * @param yearMo - Six-digit warehouse period key.
 * @returns Label or null when not parseable.
 */
export const formatYearMoToReportMonthLabel = (
  yearMo: string,
): string | null => {
  const normalized = yearMo.trim();
  if (!/^\d{6}$/.test(normalized)) {
    return null;
  }
  const year = normalized.slice(0, 4);
  const monthAbbrev = MONTH_NUM_TO_ABBREV[normalized.slice(4, 6)];
  if (!monthAbbrev) {
    return null;
  }
  return `${monthAbbrev} ${year}`;
};

/**
 * Builds consecutive `YYYYMM` keys ending at `endYearMo` (inclusive), oldest first.
 * @param endYearMo - Last month in the range.
 * @param monthCount - Number of months (e.g. 6).
 * @returns Ordered keys or null when inputs are invalid.
 */
export const buildYearMoRangeEndingAt = (
  endYearMo: string,
  monthCount: number,
): string[] | null => {
  const end = endYearMo.trim();
  if (!/^\d{6}$/.test(end) || monthCount < 1) {
    return null;
  }

  const months: string[] = [];
  let year = Number.parseInt(end.slice(0, 4), 10);
  let month = Number.parseInt(end.slice(4, 6), 10);

  for (let i = 0; i < monthCount; i += 1) {
    months.unshift(`${year}${String(month).padStart(2, '0')}`);
    month -= 1;
    if (month === 0) {
      month = 12;
      year -= 1;
    }
  }

  return months;
};

/** Default number of months in the VBP engagement growth chart when not specified. */
export const VBP_GROWTH_MONTH_COUNT = 6;

/**
 * Builds chart points for VBP six-month growth from numeric values and an end `YYYYMM`.
 * @param values - Engaged counts oldest to newest.
 * @param endYearMo - Last month in the series (`YYYYMM`).
 * @returns Points with month labels such as `Mar 2027`.
 */
export const buildVbpGrowthChartPoints = (
  values: number[],
  endYearMo: string | null,
): Array<{ month: string; engaged: number }> => {
  if (values.length === 0) {
    return [];
  }

  const yearMos =
    endYearMo !== null
      ? buildYearMoRangeEndingAt(endYearMo, values.length)
      : null;

  return values.map((engaged, index) => {
    const yearMo = yearMos?.[index];
    const month =
      yearMo !== undefined
        ? (formatYearMoToReportMonthLabel(yearMo) ?? yearMo)
        : `Month ${index + 1}`;
    return { month, engaged };
  });
};

/**
 * Resolves the end `YYYYMM` for labeling VBP growth charts from report context.
 * @param reportingPeriod - Stored report period label.
 * @param measurementPeriod - Optional warehouse measurement period from snapshot.
 * @returns End month key or current month when not parseable.
 */
export const resolveVbpGrowthChartEndYearMo = (
  reportingPeriod: string,
  measurementPeriod?: string | null,
): string => {
  const candidates = [measurementPeriod, reportingPeriod];
  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }
    const end = parseMeasurementPeriodEndYearMo(candidate);
    if (end) {
      return end;
    }
  }
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}${month}`;
};

/**
 * Maps a `YYYYMM` warehouse key to `YYYYQ#` (e.g. `202606` → `2026Q2`).
 * @param yearMo - Six-digit warehouse period key.
 * @returns Quarter key.
 */
export const yearMoToYearQtr = (yearMo: string): string => {
  const month = Number.parseInt(yearMo.slice(4, 6), 10);
  const year = yearMo.slice(0, 4);
  return `${year}Q${Math.ceil(month / 3)}`;
};

/**
 * Formats `YYYYQ#` as a report quarter label (`Q2'26`).
 * @param yearQtr - Warehouse quarter key.
 * @returns Label or null when not parseable.
 */
export const formatYearQtrToReportLabel = (yearQtr: string): string | null => {
  const match = /^(\d{4})Q([1-4])$/.exec(yearQtr.trim());
  if (!match) {
    return null;
  }
  return `Q${match[2]}'${match[1].slice(2)}`;
};

/**
 * Builds consecutive `YYYYQ#` keys ending at the quarter containing `endYearMo`.
 * @param endYearMo - Last month in the range (`YYYYMM`).
 * @param qtrCount - Number of quarters (e.g. 4).
 * @returns Ordered quarter keys or null when inputs are invalid.
 */
export const buildYearQtrRangeEndingAt = (
  endYearMo: string,
  qtrCount: number,
): string[] | null => {
  const end = yearMoToYearQtr(endYearMo.trim());
  const match = /^(\d{4})Q([1-4])$/.exec(end);
  if (!match || qtrCount < 1) {
    return null;
  }

  let year = Number.parseInt(match[1], 10);
  let qtr = Number.parseInt(match[2], 10);
  const quarters: string[] = [];

  for (let i = 0; i < qtrCount; i += 1) {
    quarters.unshift(`${year}Q${qtr}`);
    qtr -= 1;
    if (qtr === 0) {
      qtr = 4;
      year -= 1;
    }
  }

  return quarters;
};
